import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core'
import { FormBuilder, UntypedFormGroup } from '@angular/forms'
import { ApplicationType } from 'src/app/services/dtos'
import { Application } from 'src/app/account/services/application.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { NewRoleModalComponent } from 'src/app/account/components/new-role-modal/new-role-modal.component'
import { EditRoleModalComponent } from 'src/app/account/components/edit-role-modal/edit-role-modal.component'
import { NewGroupModalComponent } from 'src/app/account/components/new-group-modal/new-group-modal.component'
import { EditGroupModalComponent } from 'src/app/account/components/edit-group-modal/edit-group-modal.component'
import { EditUserModalComponent } from 'src/app/account/components/edit-user-modal/edit-user-modal.component'
import { InviteUsersModalComponent } from 'src/app/account/components/invite-users-modal/invite-users-modal.component'
import { NewApiKeyModalComponent } from 'src/app/account/components/new-api-key-modal/new-api-key-modal.component'
import { EditApiKeyModalComponent } from 'src/app/account/components/edit-api-key-modal/edit-api-key-modal.component'
import { IdpModalComponent } from 'src/app/account/components/shared/idp-modal/idp-modal.component'
import { buildMockSyncPairAccess } from '../../mocks/showcase-mock-core'
import {
  LAUNCH_TEST_SANDBOX_SCENARIOS,
  SANDBOX_CONFIG_SCENARIOS,
  SandboxScenario,
} from '../../mocks/showcase-modals-mocks'
import { TestPageMockState } from '../../showcase-mock-state'

/** Every modal and dialog in the app, mounted with mock inputs for visual review. */
@Component({
  selector: 'showcase-modals',
  templateUrl: './showcase-modals.component.html',
  styleUrls: ['./showcase-modals.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ShowcaseModalsComponent {
  // Template-based modals are opened via their ModalDialogComponent ref;
  // self-wrapped modals expose their own open() that drives an inner modal-dialog.
  @ViewChild('launchRecoveryDialog') launchRecoveryDialog: ModalDialogComponent
  @ViewChild('launchTestDialog') launchTestDialog: ModalDialogComponent
  @ViewChild('concludeRecoveryDialog') concludeRecoveryDialog: ModalDialogComponent
  @ViewChild('concludeTestDialog') concludeTestDialog: ModalDialogComponent
  @ViewChild('stopTestDialog') stopTestDialog: ModalDialogComponent
  @ViewChild('failbackDialog') failbackDialog: ModalDialogComponent
  @ViewChild('unquarantineDialog') unquarantineDialog: ModalDialogComponent
  @ViewChild('confirmActionDialog') confirmActionDialog: ModalDialogComponent
  @ViewChild('appTestConfigDialog') appTestConfigDialog: ModalDialogComponent
  @ViewChild('appTestConfigAdvancedDialog') appTestConfigAdvancedDialog: ModalDialogComponent
  @ViewChild('updateAccessDialog') updateAccessDialog: ModalDialogComponent
  @ViewChild('editIntegrationDialog') editIntegrationDialog: ModalDialogComponent
  @ViewChild('areYouSureDialog') areYouSureDialog: ModalDialogComponent
  @ViewChild('submitTicketDialog') submitTicketDialog: ModalDialogComponent
  @ViewChild('confirmTicketDialog') confirmTicketDialog: ModalDialogComponent
  @ViewChild('releaseRpsDialog') releaseRpsDialog: ModalDialogComponent
  @ViewChild('confirmManualReplicationDialog') confirmManualReplicationDialog: ModalDialogComponent
  @ViewChild('confirmEbsSnapshotDialog') confirmEbsSnapshotDialog: ModalDialogComponent
  @ViewChild('awsEventLogsDialog') awsEventLogsDialog: ModalDialogComponent
  @ViewChild('newRoleModal') newRoleModal: NewRoleModalComponent
  @ViewChild('editRoleModal') editRoleModal: EditRoleModalComponent
  @ViewChild('newGroupModal') newGroupModal: NewGroupModalComponent
  @ViewChild('editGroupModal') editGroupModal: EditGroupModalComponent
  @ViewChild('editUserModal') editUserModal: EditUserModalComponent
  @ViewChild('inviteUsersModal') inviteUsersModal: InviteUsersModalComponent
  @ViewChild('newApiKeyModal') newApiKeyModal: NewApiKeyModalComponent
  @ViewChild('editApiKeyModal') editApiKeyModal: EditApiKeyModalComponent
  @ViewChild('idpModal') idpModal: IdpModalComponent

  // Drives the [launching] input on the launch-test-dialog mock so the
  // "Preparing to launch your test..." snapshot view can be exercised.
  mockLaunchTestLaunching = false

  // Mock data for modals that need a typed input. Cast as any — these are dev-only
  // fixtures sized to render the modal, not exercise its logic.
  readonly mockRole: any = {
    roleId: 'MOCK-TEST-role',
    name: 'MOCK-TEST Role',
    description: 'A test role',
    policies: [],
  }
  readonly mockGroup: any = {
    groupId: 'MOCK-TEST-group',
    name: 'MOCK-TEST Group',
    description: 'A test group',
    users: [],
    roles: [],
  }
  readonly mockUser: any = {
    userId: 'MOCK-TEST-user',
    email: 'mock-test@example.com',
    firstName: 'Test',
    lastName: 'User',
    isSamlUser: false,
    groups: [],
  }
  readonly mockApiKey: any = { apiKeyId: 'MOCK-TEST-key', name: 'MOCK-TEST API key', accessLevel: 'admin' }
  // Field names must match IdpModalData: { type, operation, existingIdp? }. Default to
  // msEntra/edit so the dialog renders the Name + Directory ID inputs.
  readonly mockIdpData: any = {
    type: 'msEntra',
    operation: 'edit',
    existingIdp: { name: 'MOCK-TEST IdP', tenantId: 'MOCK-TEST-tenant-id' },
  }
  readonly mockIntegration: any = null

  // Stand-in HubSpot inline-message HTML for the support "ticket opened"
  // confirmation dialog so it renders without making a real HubSpot call.
  readonly mockHubspotResponse: string =
    '<p>Thanks! Your ticket has been opened. We typically reply within 4 business hours.</p>'

  readonly applicationTypeAws = ApplicationType.AWS

  // Mocks for the "Let's get you updated" (update-access) dialog so it renders
  // populated stack rows instead of empty skeleton placeholders.
  readonly mockAccessCheckResults = buildMockSyncPairAccess()
  readonly mockSourceEndpointUri = 'aws:111111111111:us-east-1'
  readonly mockTargetEndpointUri = 'aws:222222222222:us-west-2'

  readonly launchTestSandboxScenarios = LAUNCH_TEST_SANDBOX_SCENARIOS
  readonly sandboxConfigScenarios = SANDBOX_CONFIG_SCENARIOS
  launchTestSandboxKey: string = LAUNCH_TEST_SANDBOX_SCENARIOS[0].key
  sandboxConfigKey: string = SANDBOX_CONFIG_SCENARIOS[0].key

  constructor(
    private readonly state: TestPageMockState,
    private readonly formBuilder: FormBuilder
  ) {}

  // Stub form for the inlined "Open a ticket" support dialog. Mirrors the shape of
  // SupportComponent.createTicketFormModel so the template renders; submit is a no-op.
  readonly mockTicketFormModel: UntypedFormGroup = this.formBuilder.group({
    email: ['mock-test@example.com'],
    ticketPriority: ['LOW'],
    ticketSubject: ['Sample ticket subject'],
    ticketContent: ['Sample ticket description.'],
  })

  get application(): Application {
    return this.state.application
  }

  // Group a sandbox scenario list by its `group` field (AWS / Azure), preserving
  // source order, for the picker <optgroup>s.
  private groupSandboxScenarios(list: readonly SandboxScenario[]): { group: string; scenarios: SandboxScenario[] }[] {
    const groups: { group: string; scenarios: SandboxScenario[] }[] = []
    for (const scenario of list) {
      const existing = groups.find((g) => g.group === scenario.group)
      if (existing) existing.scenarios.push(scenario)
      else groups.push({ group: scenario.group, scenarios: [scenario] })
    }
    return groups
  }

  get launchTestSandboxGroups() {
    return this.groupSandboxScenarios(this.launchTestSandboxScenarios)
  }

  get sandboxConfigGroups() {
    return this.groupSandboxScenarios(this.sandboxConfigScenarios)
  }

  // -------- Modal open helpers (visual testing only) --------

  // Swap the shared `application` to the variant matching the modal's expected sync
  // phase before the modal-dialog template re-evaluates, then wait one tick so
  // Angular mounts the dialog with the right input value.
  private openWithApp(app: Application | null, dialog: ModalDialogComponent | undefined) {
    // Every non-sandbox-scenario modal expects all capabilities on. The sandbox
    // openers set mockDisabledCapabilities and bypass this helper, so clearing here
    // can't stomp their state.
    this.state.mockDisabledCapabilities.clear()
    if (app) this.state.application = app
    setTimeout(() => dialog?.open(), 0)
  }

  // Apply the scenario's disabled capabilities, swap to its pre-built anchor app,
  // then open. Kept separate from openWithApp() so the capability set survives to
  // the dialog's ngOnInit.
  private openSandboxScenario(
    app: Application | undefined,
    disabledCapabilities: string[] | undefined,
    dialog: ModalDialogComponent | undefined
  ) {
    this.state.mockDisabledCapabilities = new Set(disabledCapabilities ?? [])
    this.mockLaunchTestLaunching = false
    if (app) this.state.application = app
    setTimeout(() => dialog?.open(), 0)
  }

  openLaunchTestSandboxScenario() {
    const scenario = this.launchTestSandboxScenarios.find((s) => s.key === this.launchTestSandboxKey)
    if (!scenario) return
    this.openSandboxScenario(
      this.state.launchTestSandboxAppMap.get(scenario.key),
      scenario.disabledCapabilities,
      this.launchTestDialog
    )
  }

  openSandboxConfigScenario() {
    const scenario = this.sandboxConfigScenarios.find((s) => s.key === this.sandboxConfigKey)
    if (!scenario) return
    this.openSandboxScenario(
      this.state.sandboxConfigAppMap.get(scenario.key),
      scenario.disabledCapabilities,
      this.appTestConfigDialog
    )
  }

  // Wired to the sandbox dialogs' close/cancel so a capability-off scenario doesn't
  // leave capabilities disabled for the always-rendered background previews.
  resetMockCapabilities() {
    this.state.mockDisabledCapabilities.clear()
  }

  openLaunchRecovery() {
    this.openWithApp(this.state.mockStandbyApp, this.launchRecoveryDialog)
  }
  openLaunchRecoveryAzure() {
    this.openWithApp(this.state.mockAzureStandbyApp, this.launchRecoveryDialog)
  }
  openLaunchTest() {
    this.mockLaunchTestLaunching = false
    this.openWithApp(this.state.mockStandbyApp, this.launchTestDialog)
  }
  openConcludeRecovery() {
    this.openWithApp(this.state.mockFailoverApp, this.concludeRecoveryDialog)
  }
  openConcludeTest() {
    // Use the restore-complete app so the full conclude flow (test outcome +
    // generate report) shows; the still-restoring app would go all-cancel.
    this.openWithApp(this.state.mockFailoverTestCompletedApp, this.concludeTestDialog)
  }
  openStopTest() {
    this.openWithApp(this.state.mockFailoverTestApp, this.stopTestDialog)
  }
  openFailback() {
    this.openWithApp(this.state.mockFailoverApp, this.failbackDialog)
  }
  openUnquarantine() {
    this.openWithApp(this.state.mockFailoverApp, this.unquarantineDialog)
  }
  openUnquarantineAzure() {
    this.openWithApp(this.state.mockAzureFailoverApp, this.unquarantineDialog)
  }
  openConfirmAction() {
    this.confirmActionDialog?.open()
  }
  openAppTestConfig() {
    this.openWithApp(this.state.mockFailoverTestApp, this.appTestConfigDialog)
  }
  openAppTestConfigAdvanced() {
    this.openWithApp(this.state.mockSandboxAdvancedApp, this.appTestConfigAdvancedDialog)
  }
  openUpdateAccess() {
    this.updateAccessDialog?.open()
  }
  openEditIntegration() {
    this.editIntegrationDialog?.open()
  }
  openSubmitTicket() {
    this.submitTicketDialog?.open()
  }
  openConfirmTicket() {
    this.confirmTicketDialog?.open()
  }
  openReleaseRps() {
    this.releaseRpsDialog?.open()
  }
  openConfirmManualReplication() {
    this.confirmManualReplicationDialog?.open()
  }
  openSandboxEvents() {
    // The real opener requires advancedNetworkSandboxEnabled + failoverTest phase,
    // so seed the mock with the matching variant before the dialog mounts.
    this.state.mockFirewallEventsPopulated = false
    this.openWithApp(this.state.mockSandboxAdvancedApp, this.awsEventLogsDialog)
  }
  openSandboxEventsPopulated() {
    this.state.mockFirewallEventsPopulated = true
    this.openWithApp(this.state.mockSandboxAdvancedApp, this.awsEventLogsDialog)
  }
  openConfirmEbsSnapshot() {
    this.confirmEbsSnapshotDialog?.open()
  }
  openAreYouSure() {
    this.openWithApp(this.state.mockStandbyApp, this.areYouSureDialog)
  }
  openNewRole() {
    this.newRoleModal?.open()
  }
  openEditRole() {
    this.editRoleModal?.open(this.mockRole)
  }
  openNewGroup() {
    this.newGroupModal?.open()
  }
  openEditGroup() {
    this.editGroupModal?.open(this.mockGroup)
  }
  openEditUser() {
    this.editUserModal?.open(this.mockUser)
  }
  openInviteUsers() {
    this.inviteUsersModal?.open()
  }
  openNewApiKey() {
    this.newApiKeyModal?.open()
  }
  openEditApiKey() {
    this.editApiKeyModal?.open(this.mockApiKey)
  }
  openIdpModal() {
    this.idpModal?.open(this.mockIdpData)
  }
}
